<script lang="ts">
	import { format, locale } from 'svelte-i18n';
	import { save_home } from 'src/helpers/home_helpers';
	import { home_data, user_data } from 'src/stores/account/user';
	import { navigateToShop } from '../Lists/list-helpers';
	import { ButtonV2, Container, Text } from '@Shelly/shelly-design-system';
	import { getPromoExpiryText } from 'src/helpers/promo_helpers';
	import { PROMOTIONS } from 'src/constants';

	import PromoModal from 'src/components/Promotions/organisms/PromoModal.svelte';
	import PromoModalBanner from 'src/components/Promotions/molecules/PromoModalBanner.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import { createEventDispatcher } from 'svelte';

	export let showModal = false;

	const dispatch = createEventDispatcher();

	const onCloseModal = (fromShopButton = false) => {
		if (showModal) {
			showModal = false;
			if (!$home_data?.promotions?.xlX2FlashDeal) {
				save_home('promotions', {
					...$home_data?.promotions,
					xlX2FlashDeal: new Date().getTime(),
				});
			}
			if (!fromShopButton) {
				const xlx2 = { ...$home_data?.aep?.xlx2 };
				xlx2.closeOnPromotionModal = (xlx2?.closeOnPromotionModal || 0) + 1;
				save_home('aep', { ...$home_data.aep, xlx2 });
			}
			dispatch('close');
		}
	};

	const handleSaveForLater = () => {
		onCloseModal();
	};

	const handleOpenShop = () => {
		onCloseModal(true);
		const xlx2 = { ...$home_data?.aep?.xlx2 };
		xlx2.clickOnShopNowButton = (xlx2?.clickOnShopNowButton || 0) + 1;
		save_home('aep', { ...$home_data.aep, xlx2 });
		if ($user_data?.timezone.startsWith('America/')) {
			navigateToShop(
				'https://www.shelly.com/de/collections/smart-controllers?utm_source=shellyapp&utm_medium=referral&utm_campaign=wall_display_flash_sale',
			);
		} else {
			navigateToShop(
				`https://www.shelly.com/${['de', 'fr', 'it'].includes($locale) ? `${$locale}/` : ''}collections/smart-controllers?utm_source=shellyapp&utm_medium=referral&utm_campaign=wall_display_flash_sale`,
			);
		}
	};

	$: expiryText = $format(
		getPromoExpiryText(PROMOTIONS.XL_X2_FLASH_DEAL.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.XL_X2_FLASH_DEAL.END_DATE)?.object,
	);

	$: image = './images/xl_x2_flash_deal_default_99.png';
</script>

<PromoModal
	{showModal}
	title={$format('title_wall_display_flash_sale', { default: 'WallDisplay Flash Sale' })}
	closeAriaLabel={$format('button_close', { default: 'Close' })}
	on:close={handleSaveForLater}
	backdropClose
>
	<PromoModalBanner
		{image}
		alt={$format('title_wall_display_flash_sale', { default: 'WallDisplay Flash Sale' })}
		activeBadgeLabel={$format('label_active', { default: 'Active' })}
		overlayPosition="top"
		{expiryText}
	/>

	<div class="content-section">
		<div class="content-text">
			<p class="content-paragraph text-bold">
				{$format('text_wall_display_flash_sale_intro', {
					default: 'Your Devices Deserve a Screen.',
				})}
			</p>
			<p class="content-paragraph text-300">
				{$format('text_wall_display_flash_sale_hurry', {
					default:
						'Every device, scene, and sensor - controlled from the wall with a single touch. No phone needed. Pick your size, use your code, save up to €91.',
				})}
			</p>
			<Container type="primary-10" class="mb-s-plus text-center">
				<Text class="mb-s text-uppercase" styleType="tiny"
					>{$format('text_wall_display_flash_sale_promo_code', {
						default: 'Promo code:',
					})}</Text
				>
				<Text styleType="h1">WDFLASH48</Text>
			</Container>
			<Text styleType="miniature" class="text-300 text-uppercase">
				{$format('text_wall_display_flash_sale_terms', { default: 'Terms & Conditions:' })}
			</Text>
			<Text styleType="miniature" class="text-300 text-uppercase">
				{$format('text_wall_display_flash_sale_terms_text', {
					default:
						'This discount code is valid exclusively for Shelly Wall Display, Wall Display X2i, and Wall Display XL. The code expires on April 1, 2026, regardless of the date of receipt. It cannot be combined with other discounts. The code is for personal, one-time, free-of-charge use only. It is non-transferable, not shareable, and not redeemable for cash.',
				})}
			</Text>
		</div>
	</div>

	<svelte:fragment slot="footer">
		<div class="width-full d-flex justify-content-end gap-s">
			<ButtonV2
				variant="secondary"
				on:click={handleSaveForLater}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			>
				{capFirstChar($format('button_maybe_later', { default: 'Maybe later' }))}
			</ButtonV2>
			<ButtonV2
				variant="primary"
				on:click={handleOpenShop}
				icon="fas fa-shop"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			>
				{$format('button_shop_now', { default: 'Shop Now' })}
			</ButtonV2>
		</div>
	</svelte:fragment>
</PromoModal>

<style>
	.content-section {
		display: flex;
		flex-direction: column;
		gap: 24px;
		padding: 24px 24px 0px 24px;
	}

	.content-text {
		font-family: 'Inter', sans-serif;
		font-size: 16px;
		font-weight: 400;
		line-height: 19px;
		color: var(--color-white, #ffffff);
	}

	.content-paragraph {
		margin: 0 0 12px 0;
	}

	@media (max-width: 639px) {
		.content-section {
			padding: 16px 16px 0px 16px;
		}
	}
</style>
